import type React from "react";
import {Alert} from "@mui/material";
import {Tag} from "~/components/tag/Tag.tsx";
import type {ContractDetail} from "../contractExplorerTypes.ts";
import {formatDate, typeLabel} from "../contractExplorerFormat.ts";
import {KeyValue} from "./KeyValue.tsx";
import {Section} from "./Section.tsx";

const STATE = {
    linked: {label: 'Linked', color: 'success'},
    no_spend: {label: 'No spend in scope', color: 'default'},
    not_ready: {label: 'Not ready', color: 'error'},
    failed: {label: 'Failed', color: 'error'},
    '': {label: 'Not linked', color: 'error'},
} as const;

const METHOD: Record<string, string> = {
    structured: 'Every transaction in scope read in one answer',
    sql: 'Rules per contract line, applied to the transactions in scope',
};

/** How linkage read the spend for this bundle: what it matched, the scope it cut, and how it linked. */
export const MatchingBasis: React.FC<{contract: ContractDetail}> = ({contract}) => {
    const linkage = contract.linkage;
    const state = STATE[linkage.state] ?? STATE[''];
    return (
        <Section title="Matching basis" right={<Tag label={state.label} color={state.color}/>}
                 sub="Linkage ran in cat-contract-extract; this is what it found. Nothing here can be changed.">
            <div className="ci-card ci-kv">
                <KeyValue label="Supplier in the spend data">
                    {linkage.supplier_label || <span className="ci-dash">—</span>}
                </KeyValue>
                <KeyValue label="Buying entities in the spend data">
                    {linkage.entities.length ? linkage.entities.join(' · ') : <span className="ci-dash">—</span>}
                </KeyValue>
                <KeyValue label="Spend from" note="Transactions dated before the bundle's start are out of scope">
                    {formatDate(linkage.scope_from)}
                </KeyValue>
                <KeyValue label="Transactions in scope">{linkage.rows_in_scope.toLocaleString('en-GB')}</KeyValue>
                <KeyValue label="Method">{METHOD[linkage.method] ?? typeLabel(linkage.method)}</KeyValue>
                {linkage.missing.length > 0 && (
                    <KeyValue label="Missing">{linkage.missing.map(typeLabel).join(' · ')}</KeyValue>
                )}
            </div>
            {linkage.summary && <p className="ci-narr">{linkage.summary}</p>}
            {linkage.warnings.map(warning => <Alert key={warning} severity="warning">{warning}</Alert>)}
        </Section>
    );
};
